import { forwardRef, type HTMLAttributes, type ElementType } from 'react';
import { cn } from '../utils/cn.js';

export type SectionVariant = 'hero' | 'band' | 'feature' | 'inkwell' | 'cta' | 'trust';
export type SectionBg = 'canvas' | 'surface' | 'subtle' | 'inkwell' | 'paper';
export type SectionWidth = 'default' | 'narrow' | 'wide';

export interface SectionProps extends HTMLAttributes<HTMLElement> {
  /** Semantic-layout variant. Controls vertical padding. */
  variant?: SectionVariant;
  /** Background surface. Defaults to canvas (no bg). */
  bg?: SectionBg;
  /** Container max-width. `default` = max-w-page-wide (~1200px). */
  width?: SectionWidth;
  /** Render as a different element (default `section`). */
  as?: ElementType;
  /** Optional id for anchor links. */
  id?: string;
}

/**
 * `<Section>` wraps the marketing-page rhythm boilerplate:
 *   `mx-auto max-w-page-wide px-5 py-16 lg:px-10 lg:py-24`
 *
 * Enforces consistent vertical padding across marketing pages so section
 * rhythm doesn't drift between routes. Pass `bg` to swap surfaces without
 * hand-writing inline styles.
 *
 * Padding by variant (mobile / desktop):
 *   hero    :   pt-12  pt-20   pb-16 pb-24   (bigger top gap to breathe)
 *   band    :   py-16  py-24   (default)
 *   feature :   py-16  py-24
 *   inkwell :   py-16  py-24
 *   cta     :   py-16  py-24
 *   trust   :   py-12  py-16   (denser, less breathing)
 */
const VARIANT_PADDING: Record<SectionVariant, string> = {
  hero:    'pt-12 pb-16 lg:pt-20 lg:pb-24',
  band:    'py-16 lg:py-24',
  feature: 'py-16 lg:py-24',
  inkwell: 'py-16 lg:py-24',
  cta:     'py-16 lg:py-24',
  trust:   'py-12 lg:py-16',
};

const BG_CLASS: Record<SectionBg, string> = {
  canvas:  '',
  surface: 'bg-primary',                                   // token — light card
  subtle:  'bg-secondary',                                 // page canvas alt
  inkwell: 'inkwell',                                      // dark hero band
  paper:   'paper',                                        // offer-letter cream
};

const WIDTH_CLASS: Record<SectionWidth, string> = {
  default: 'max-w-page-wide',
  narrow:  'max-w-page-narrow',
  wide:    'max-w-none',
};

export const Section = forwardRef<HTMLElement, SectionProps>(function Section(
  { variant = 'band', bg = 'canvas', width = 'default', as, id, className, children, ...rest },
  ref,
) {
  const Tag = (as ?? 'section') as ElementType;
  return (
    <Tag
      ref={ref as never}
      id={id}
      className={cn(BG_CLASS[bg], VARIANT_PADDING[variant])}
      {...rest}
    >
      <div className={cn('mx-auto px-5 lg:px-10', WIDTH_CLASS[width], className)}>
        {children}
      </div>
    </Tag>
  );
});
